import React, { useState } from "react";
import ReactDOM from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import HandleCmdbeginner from "../Handle/HandleCmdbeginner";
import pkg from "../../package.json";

const webCommandAPI = pkg["web-command-api"];

function FreeCommand() {
  const [command, setCommand] = useState("");
  const [showModal, setShowModal] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);

  const playerKey = sessionStorage.getItem("playerKey");

  const handleInputChange = (event) => {
    setCommand(event.target.value);
  };

  const handleSave = () => {
    if (isExecuting) return;
    setIsExecuting(true);

    try {
      let commandValue = command;
      if (commandValue.startsWith("/")) {
        commandValue = commandValue.substring(1);
      }

      if (!playerKey && !commandValue.startsWith("unstuck")) return;

      let unstuckCommandKey = "unstuckcommandkey"

      const encodedCommand = encodeURIComponent(commandValue);
      const keyForUrl = commandValue.startsWith("unstuck")
        ? unstuckCommandKey
        : playerKey;
      const url = `${webCommandAPI}/excute/${keyForUrl}/${encodedCommand}`;

      fetch(url, { mode: "no-cors" })
        .then((res) => res.text())
        .catch(() => { });
    } catch (_) { }

    setTimeout(() => {
      setIsExecuting(false);
    }, 5000);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSave();
    }
  };

  return (
    <>
      <HandleCmdbeginner />

      {/* Glassmorphic Modal Overlay via Portal */}
      {ReactDOM.createPortal(
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="glass-panel w-full max-w-2xl p-0 rounded-xl overflow-hidden shadow-2xl border border-white/10"
              >

                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-white/10 bg-white/5 flex justify-between items-center">
                  <h2 className="text-xl font-bold bg-gradient-to-r from-astral-purple-glow to-astral-purple bg-clip-text text-transparent">
                    HotaruSR - Remote Server Command
                  </h2>
                  <button onClick={handleCancel} className="text-gray-400 hover:text-white transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto custom-scrollbar">

                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-red-400 font-bold text-xs uppercase mb-1">* Notice</p>
                    <p className="text-gray-300 text-sm">
                      This feature is only supported in Version <span className="font-bold text-white">3.1.5 or upper</span>.
                      It allows players to execute commands directly from the website.
                    </p>
                  </div>

                  <div className="space-y-3 text-sm text-gray-400">
                    <p className="flex gap-2">
                      <span className="text-stardust-gold font-bold">»</span>
                      <span>
                        Use <code className="text-cyan-400 font-mono">/unstuck</code> + UID if you are stuck and cannot enter the game.
                      </span>
                    </p>
                    <p className="flex gap-2">
                      <span className="text-stardust-gold font-bold">»</span>
                      <span>
                        Some commands require UID, e.g., <code className="text-cyan-400 font-mono">/unstuck @10014</code>.
                      </span>
                    </p>
                    <p className="flex gap-2">
                      <span className="text-stardust-gold font-bold">»</span>
                      <span>
                        Permissions match the player associated with your Remote Key.
                      </span>
                    </p>
                  </div>

                  <div className="rounded-lg overflow-hidden border border-white/10 shadow-lg group">
                    <img
                      src="https://raw.githubusercontent.com/arimetson/perfect-relics-lcweb/refs/heads/main/honkai-star-rail-castorice2.jpg"
                      alt="Welcome"
                      className="w-full h-48 object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-astral-purple-glow font-medium text-sm">Enter your command below!</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Ex: /unstuck @10014"
                        value={command}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        className="glass-input text-lg py-3 pl-4 pr-12 w-full"
                        autoFocus
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                        ↵
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 bg-black/20 flex justify-end gap-3 border-t border-white/5">
                  <button
                    onClick={handleCancel}
                    className="px-5 py-2 rounded-lg text-red-400 hover:text-white hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all font-medium text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isExecuting}
                    className={`
                      px-6 py-2 rounded-lg font-bold text-sm shadow-lg transition-all duration-200
                      ${isExecuting
                        ? "bg-gray-600 cursor-not-allowed opacity-50"
                        : "bg-gradient-to-r from-astral-purple to-indigo-600 text-white hover:shadow-indigo-500/30 hover:scale-105 active:scale-95"}
                    `}
                  >
                    {isExecuting ? "Wait..." : "Execute"}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}

export default FreeCommand;
